import moment from 'moment-timezone';
import { isNil } from 'lodash-es';
import { createWithZone, formatDuration, toMomentWeekday } from '~/utils/time_utils';
import { INSIGHTS } from '~/app_constants';

function getInsightsPreferences(orgId) {
  if (!orgId) {
    return {};
  }
  try {
    return JSON.parse(localStorage.getItem(`insights_preferences_${orgId}`)) || {};
  } catch {
    return {};
  }
}

export function getInsightsPreference(orgId, key) {
  return getInsightsPreferences(orgId)[key] ?? null;
}

export function setInsightsPreference(orgId, key, value) {
  if (!orgId) {
    return;
  }
  try {
    const prefs = getInsightsPreferences(orgId);

    prefs[key] = value;
    localStorage.setItem(`insights_preferences_${orgId}`, JSON.stringify(prefs));
  } catch {
    // localStorage unavailable
  }
}

// Current lower limit of Insights.
export const INSIGHTS_LOWER_LIMIT_MONTHS = 6;

export const getInsightsLowerLimit = ({ now, timeZone, useRetentionLimit, activityRetentionStart }) => {
  if (useRetentionLimit && activityRetentionStart) {
    return createWithZone(activityRetentionStart, null, timeZone).startOf('day');
  }

  const base = now ? now.clone() : moment().tz(timeZone);

  return base.startOf('day').subtract(INSIGHTS_LOWER_LIMIT_MONTHS, 'months');
};

export const isBeforeInsightsLowerLimit = (date, lowerLimit) => {
  return date.clone().startOf('day').isBefore(lowerLimit, 'day');
};

export const clampToInsightsLowerLimit = (date, lowerLimit) => {
  return isBeforeInsightsLowerLimit(date, lowerLimit) ? lowerLimit.clone() : date;
};

export const proficiencyClasses = (score, prefix = 'proficiency') => {
  let scoreClass = 'unknown';

  if (score !== null) {
    scoreClass = score > 50 ? 'core' : 'unproductive';
  }

  return `${prefix}-${scoreClass}`;
};

export const activityClasses = (activity, prefix = 'activity') => {
  let activityClass = 'none';

  if (activity > 50) {
    activityClass = 'high';
  } else if (activity > 20) {
    activityClass = 'medium';
  } else if (activity) {
    activityClass = 'low';
  }

  return `${prefix}-${activityClass}`;
};

export const metricColorClass = (value) => {
  if (isNil(value)) {
    return 'empty';
  }

  if (value === 0) {
    return 'same';
  }

  return value > 0 ? 'positive' : 'negative';
};

export const numericValue = (number, { maxNumberBeforeSymbol = 99999, roundDigits = 0 } = {}) => {
  return number > maxNumberBeforeSymbol ? `${(number / 1000).toFixed(roundDigits)}k` : number;
};

export const parseMetricValue = (value, type) => {
  if (isNil(value)) {
    return '—';
  }

  const abs = Math.abs(value);

  if (type === 'percentage') {
    return `${Math.round(abs)}%`;
  }
  if (type === 'time') {
    return formatDuration(abs, { showSeconds: 'never' });
  }
  if (type === 'number') {
    return Math.round(abs);
  }

  return value;
};

/*
 * Transforms the combination between 'from' and 'period' into the proper
 * 'id' field so that we can correctly set the date filter by evaluating the matching
 * query parameters.
 *
 * E.g.: a period of 'week' and a date that doesn't match the current week will
 * output 'last-week'.
 */

export const getPeriodIdFromPeriod = (period = 'month', from, startWeekOn = 0) => {
  const isWeekPeriod = period === 'week';
  const startOfWeek = toOrganizationWeekDay(moment(), null, startWeekOn, 'start');
  const startOfCurrentPeriod = isWeekPeriod ? startOfWeek : moment().startOf(period);
  const diff = isWeekPeriod
    ? moment(from).isBefore(startOfCurrentPeriod)
    : moment(from).startOf(period).diff(startOfCurrentPeriod, 'days');
  const periodKey = diff ? `last-${period}` : `this-${period}`;

  return (INSIGHTS.filters.date.find((d) => d.id === periodKey) || INSIGHTS.filters.date[0]).id;
};

/**
 * Calculates the start or end of the week, accounting for what is considered the first day of the week in
 * the current organization.
 * This is necessary so that if an organization decides that the week begins on a non-ISO
 * day (Monday), we can shift the start/end of said week accordingly.
 *
 * We use 'isoWeek' instead of 'week' to prevent moment.js from localizing the calendar.
 *
 * @param {moment} date The date to use as the base for calculations.
 * @param {moment} compareWith Whether to use a base date to do calculations or not. If not, 'start' will be used.
 * @param {number} weekStartsOn The first day of the week (organization setting). Defaults to Monday (ISO).
 * @param {string} type Whether to calculate the 'start' or the 'end' of the week.
 * @return {moment} The start/end moment.js object.
 */

export function toOrganizationWeekDay(date = moment(), compareWith = null, weekStartsOn = 1, type = 'start') {
  let offset = 0;
  const now = new Date();
  const isStart = type === 'start';
  const converted = toMomentWeekday(weekStartsOn);

  now.setHours(0, 0, 0, 0);

  // How many days are we offsetting to the default ISO start of week (Monday)?
  // Only necessary if we're NOT on Monday already
  if (converted !== 1) {
    offset = converted - 1;
  }

  const start = date.clone().startOf('isoWeek').add(offset, 'days');
  const end = date.clone().endOf('isoWeek').add(offset, 'days').add(1, 'second');
  const isAfter = compareWith ? compareWith.add(1, 'week').isAfter(now) : start.isAfter(now);

  if (isAfter) {
    return isStart ? start.clone().subtract(1, 'week') : end.clone().subtract(1, 'week');
  }

  return isStart ? start : end;
}
